import { AlertRibbon, Button, RadioButton } from "@sebgroup/green-react";
import { calculatePeriodFeeThunk, loadPageThunk, updateFormThunk } from "../slices/calculatorActionCreators";
import { complete, validateForm } from "../slices/calculatorSlice";
import React, { ReactNode, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ToggleSelectorGroup from "../../components/Form/ToggleSelector/ToggleSelectorGroup";
import { useTranslation } from "react-i18next";
import { getSelectedContractLengthInMonths, selectPageState } from "../slices/calculatorSelectors";
import { IValidator } from "@sebgroup/extract";
import { showSlideoutThunk } from "../../app/slices/appActionCreators";
import Spinner from "../../components/Loaders/Spinner";
import { GreenNumberInput } from "../../components/Form/Input/GreenInput";
import { formatAmount, formatAmountRounded } from "../../utils/formatters";
import { selectAppLanguage, selectAppNumberFormat } from "../../app/slices/appSelectors";
import { getMaxNumberInputLength, getSliderStepValue, isNumber } from "../../utils/utils";
import GreenSlider from "../../components/Form/Slider/GreenSlider";
import PreliminaryPeriodFeeCard from "./PreliminaryPeriodFeeCard";
import ArrowRight from "../../components/Icons/ArrowRightIcon";
import ErrorAlertRibbon from "../../components/ErrorAlertRibbon/ErrorAlertRibbon";
import { useIsMobile } from "../../hooks/mediaHooks";

const CONTRACT_PERIOD_IN_MONTHS_MAX_COLUMNS = 4;
const RESIDUAL_VALUE_PERCENTAGE_MAX_COLUMNS = 3;

function CalculatorPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectPageState);
  const numberFormat = useAppSelector(selectAppNumberFormat);
  const selectedContractLengthInMonths = useAppSelector(getSelectedContractLengthInMonths);
  const language = useAppSelector(selectAppLanguage);
  const isMobile = useIsMobile();
  const hasPageLoaded = useRef(false);

  const purchasePriceInRange = (): boolean => {
    return (
      state.financingStructure !== undefined &&
      !!state.financingStructure.purchasePriceFrom &&
      !!state.financingStructure.purchasePriceTo &&
      isNumber(state.form.purchasePrice) &&
      state.form.purchasePrice >= state.financingStructure.purchasePriceFrom &&
      state.form.purchasePrice <= state.financingStructure.purchasePriceTo
    );
  };

  const purchasePriceAboveMinimum = (): boolean => {
    return (
      state.financingStructure !== undefined &&
      !!state.financingStructure.purchasePriceFrom &&
      isNumber(state.form.purchasePrice) &&
      state.form.purchasePrice >= state.financingStructure.purchasePriceFrom
    );
  };

  const maxNumberInputLength = getMaxNumberInputLength(state.financingStructure?.purchasePriceTo);
  const minDownPayment =
    state.form.purchasePrice && selectedContractLengthInMonths?.downPaymentRange && purchasePriceAboveMinimum()
      ? (state.form.purchasePrice * selectedContractLengthInMonths?.downPaymentRange.rangeFrom) / 100
      : 0;
  const maxDownPayment =
    state.form.purchasePrice && selectedContractLengthInMonths?.downPaymentRange && purchasePriceAboveMinimum()
      ? (state.form.purchasePrice * selectedContractLengthInMonths?.downPaymentRange.rangeTo) / 100
      : undefined;

  const heading_leasingcalculator = t("heading_leasingcalculator");
  const text_aboutleasingcalculator = t("text_aboutleasingcalculator");
  const label_monthsshort = t("label_monthsshort");
  const label_monthslong = t("label_monthslong");
  const label_kr_currencyshort = t("label_kr_currencyshort");
  const label_kr_currencylong = t("label_kr_currencylong");
  const label_objectcar = t("label_objectcar");
  const label_purchasepriceexclvatquestion = t("label_purchasepriceexclvatquestion");
  const label_purchasepriceexclvatinfo = t("label_purchasepriceexclvatinfo", {
    MinPurchasePrice: formatAmount(state.financingStructure?.purchasePriceFrom, numberFormat),
    CurrencyLong: label_kr_currencylong,
  });
  const label_downpaymentquestion = t("label_downpaymentquestion");
  const label_downpaymentinfo = t("label_downpaymentinfo", {
    RecommendedDownPaymentPrice:
      state.form.purchasePrice && selectedContractLengthInMonths?.downPaymentRange.defaultValue && purchasePriceInRange()
        ? formatAmountRounded((state.form.purchasePrice * selectedContractLengthInMonths.downPaymentRange.defaultValue) / 100, numberFormat)
        : "-",
    DefaultDownPaymentPercentage: selectedContractLengthInMonths?.downPaymentRange.defaultValue,
  });
  const label_downpaymentsummary = t("label_downpaymentsummary");
  const text_downpaymentsummary = t("text_downpaymentsummary", {
    Object: label_objectcar,
    DefaultDownPaymentPercentage: selectedContractLengthInMonths?.downPaymentRange.defaultValue,
    MaxDownPaymentPercentage: selectedContractLengthInMonths?.downPaymentRange.rangeTo,
  });
  const label_leasingperiodquestion = t("label_leasingperiodquestion");
  const label_residualvaluequestion = t("label_residualvaluequestion");
  const label_residualvalueinfo = t("label_residualvalueinfo", { RecommendedResidualValue: state.form.recommendedResidualValuePercentage });
  const label_residualvaluesummary = t("label_residualvaluesummary");
  const text_residualvaluesummary = t("text_residualvaluesummary");
  const error_errorcode = t("error_errorcode");
  const info_errorinapplication = t("info_errorinapplication");
  const error_somethingwrong = t("error_somethingwrong");
  const button_reload = t("button_reload");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    dispatch(updateFormThunk({ name, value }));
    dispatch(calculatePeriodFeeThunk());
  };

  const handleBlur = () => {
    dispatch(validateForm());
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    dispatch(updateFormThunk({ name, value }));
  };

  const handleSliderClamp = () => {
    dispatch(calculatePeriodFeeThunk());
  };

  const handleShowSlideout = (payload: { header: string; body: string }) => {
    dispatch(showSlideoutThunk(payload));
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleComplete = () => {
    dispatch(complete());
  };

  const formatSliderMinMaxAmount = (value: number | undefined, unit?: string): string => {
    if (value === undefined) {
      return `- ${unit}`;
    }

    if (unit) {
      return `${formatAmountRounded(value, numberFormat)} ${unit}`;
    }

    return formatAmountRounded(value, numberFormat);
  };

  const formatErrorMessage = (fieldName: string, context?: any): IValidator | undefined => {
    if (state.form.validationErrors && state.form.validationErrors[fieldName]) {
      return {
        indicator: "error",
        message: t(state.form.validationErrors[fieldName], context) as unknown as string,
      };
    }
  };

  useEffect(() => {
    if (!hasPageLoaded.current) {
      dispatch(loadPageThunk());
      hasPageLoaded.current = true;
    }
  }, [dispatch]);

  const FinancingStructureError = () => {
    return (
      <div>
        <AlertRibbon type="danger" header={error_errorcode} isCloseable={false} aria-live="polite" role="alert">
          {state.form.loadingFinancingStructureError}
        </AlertRibbon>
        <p className="error-info">{info_errorinapplication}</p>
        <Button type="button" variant="tertiary" onClick={handleReload}>
          {button_reload} <ArrowRight />
        </Button>
      </div>
    );
  };

  const renderAvailableContractPeriodInMonthsOptions = (): Array<ReactNode> | undefined => {
    return state.availableContractPeriods?.map((availableContractPeriod) => (
      <RadioButton
        label={`${availableContractPeriod} ${isMobile ? label_monthslong : label_monthsshort}`}
        value={availableContractPeriod.toString()}
        key={availableContractPeriod}
      />
    ));
  };

  const renderAvailableResidualPercentageValueOptions = (): Array<ReactNode> | undefined => {
    return state.availableResidualPercentageValues?.map((availableResidualPercentageValue) => (
      <RadioButton
        label={`${availableResidualPercentageValue}%${isMobile ? " " : "\n"}(${
          state.form.purchasePrice && purchasePriceInRange()
            ? formatAmountRounded((state.form.purchasePrice * availableResidualPercentageValue) / 100, numberFormat)
            : "-"
        } ${label_kr_currencyshort})`}
        value={availableResidualPercentageValue.toString()}
        key={availableResidualPercentageValue}
      />
    ));
  };

  return (
    <>
      <div className="mfe-header">
        <h2>{heading_leasingcalculator}</h2>
        <p>{text_aboutleasingcalculator}</p>
      </div>

      <div className="mfe-content">
        <div className={`form row gx-lg-7 gx-0 ${state.form.loadingFinancingStructure && "loading"}`}>
          {state.form.loadingFinancingStructure && <Spinner />}

          {!state.form.loadingFinancingStructure && state.form.loadingFinancingStructureError && <FinancingStructureError />}

          {!state.form.loadingFinancingStructure && !state.form.loadingFinancingStructureError && (
            <>
              <div className="form__inputs col-lg-7 col-md-8 col-12">
                <div className="col-sm-6 col-12 mb-6">
                  <GreenNumberInput
                    id="purchase-price"
                    testId="purchase-price__testid"
                    name="purchasePrice"
                    label={label_purchasepriceexclvatquestion}
                    info={label_purchasepriceexclvatinfo}
                    unitLabel={label_kr_currencyshort}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    numberType="amount"
                    autoComplete="off"
                    maxLength={maxNumberInputLength}
                    value={formatAmount(state.form.purchasePrice, numberFormat)}
                    validator={formatErrorMessage("purchasePrice", {
                      MinPurchasePrice: formatAmount(state.financingStructure?.purchasePriceFrom, numberFormat),
                      MaxPurchasePrice: formatAmount(state.financingStructure?.purchasePriceTo, numberFormat),
                      CurrencyLong: label_kr_currencylong,
                    })}
                  />
                </div>

                <GreenSlider
                  id="down-payment"
                  testId="down-payment__testid"
                  name="downPayment"
                  label={label_downpaymentquestion}
                  info={
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: label_downpaymentinfo }}
                    />
                  }
                  min={minDownPayment}
                  max={maxDownPayment}
                  maxLength={maxNumberInputLength}
                  step={getSliderStepValue(state.form.purchasePrice)}
                  showMinMax
                  minMaxFormat={formatSliderMinMaxAmount}
                  hasTextbox
                  unitLabel={label_kr_currencyshort}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onSliderChange={handleSliderChange}
                  onSliderClamp={handleSliderClamp}
                  value={formatAmount(state.form.downPayment, numberFormat)}
                  slideoutButtonLabel={label_downpaymentsummary}
                  onSlideoutButtonClick={() =>
                    handleShowSlideout({
                      header: label_downpaymentsummary,
                      body: text_downpaymentsummary,
                    })
                  }
                  validator={formatErrorMessage("downPayment", {
                    MaxDownPayment: formatAmountRounded(maxDownPayment, numberFormat),
                    MaxDownPaymentPercentage: selectedContractLengthInMonths?.downPaymentRange.rangeTo,
                    CurrencyLong: label_kr_currencylong,
                  })}
                />

                <ToggleSelectorGroup
                  id="contract-period-in-months"
                  name="contractPeriodInMonths"
                  label={label_leasingperiodquestion}
                  onChange={handleChange}
                  valueSelected={state.form.contractPeriodInMonths?.toString()}
                  horizontal
                  maxColumns={CONTRACT_PERIOD_IN_MONTHS_MAX_COLUMNS}
                  validator={formatErrorMessage("contractPeriodInMonths")}
                  className="toggle-selector-group"
                >
                  {renderAvailableContractPeriodInMonthsOptions()}
                </ToggleSelectorGroup>

                <ToggleSelectorGroup
                  id="residual-value-percentage"
                  name="residualValuePercentage"
                  label={label_residualvaluequestion}
                  labelInformation={
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: label_residualvalueinfo }}
                    />
                  }
                  onChange={handleChange}
                  valueSelected={state.form.residualValuePercentage?.toString()}
                  horizontal
                  maxColumns={RESIDUAL_VALUE_PERCENTAGE_MAX_COLUMNS}
                  validator={formatErrorMessage("residualValuePercentage")}
                  className="toggle-selector-group"
                  slideoutButtonLabel={label_residualvaluesummary}
                  onSlideoutButtonClick={() =>
                    handleShowSlideout({
                      header: label_residualvaluesummary,
                      body: text_residualvaluesummary,
                    })
                  }
                >
                  {renderAvailableResidualPercentageValueOptions()}
                </ToggleSelectorGroup>
              </div>

              <div className="form__preliminary-period-fee-card col-lg-5 col-md-8 col-12">
                {state.form.loadingPeriodFeeError && <ErrorAlertRibbon errorMessage={error_somethingwrong} />}

                <PreliminaryPeriodFeeCard
                  loading={state.form.loadingPeriodFee}
                  periodFee={state.form.periodFee}
                  numberFormat={numberFormat}
                  language={language}
                  onComplete={handleComplete}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default CalculatorPage;
